import { cn } from '@lib/util/cn';
import {
  CheckCircleOutlined,
  InfoCircle,
  TriangleWarning,
} from '@modules/common/icons';
import type { ComponentProps, ReactNode } from 'react';

type AlertProps = ComponentProps<'div'> & {
  variant: 'info' | 'danger' | 'warning' | 'success';
  children: ReactNode;
  action?: ReactNode;
  border?: boolean;
  richText?: boolean;
};

const config = {
  info: {
    container: 'bg-brand-tertiary-100 border-brand-tertiary-800',
    textColor: 'text-brand-tertiary-800',
    iconSize: '24' as const,
    textClass: 'typography-paragraph-xs',
    align: 'items-start',
    gap: 'gap-2.5',
    padding: 'px-4 py-2',
    border: false,
  },
  danger: {
    container: 'bg-brand-danger-50 border-brand-danger-600',
    textColor: 'text-brand-danger-800',
    iconSize: '24' as const,
    textClass: 'typography-paragraph',
    align: 'items-start',
    gap: 'gap-2.5',
    padding: 'px-4 py-2',
    border: true,
  },
  warning: {
    container: 'bg-brand-warning-50 border-brand-warning-600',
    textColor: 'text-brand-warning-900',
    iconSize: '24' as const,
    textClass: 'typography-paragraph',
    align: 'items-start',
    gap: 'gap-2.5',
    padding: 'px-4 py-2',
    border: true,
  },
  success: {
    container: 'bg-brand-success-50 border-brand-success-600',
    textColor: 'text-brand-success-800',
    iconSize: '24' as const,
    textClass: 'typography-paragraph',
    align: 'items-start',
    gap: 'gap-2.5',
    padding: 'px-4 py-2',
    border: true,
  },
} as const;

const icons = {
  info: InfoCircle,
  danger: TriangleWarning,
  warning: TriangleWarning,
  success: CheckCircleOutlined,
} as const;

export function Alert({
  variant,
  children,
  action,
  border,
  richText,
  className,
  ...rest
}: AlertProps) {
  const {
    container,
    textColor,
    iconSize,
    textClass,
    align,
    gap,
    padding,
    border: defaultBorder,
  } = config[variant];

  const Icon = icons[variant];
  const showBorder = border ?? defaultBorder;

  return (
    <div
      className={cn(
        'flex flex-col gap-2 rounded-2 w-full',
        showBorder && 'border',
        padding,
        container,
        className,
      )}
      role={variant === 'danger' || variant === 'warning' ? 'alert' : 'status'}
      {...rest}
    >
      <div className={cn('flex', align, gap)}>
        <Icon size={iconSize} className={cn('shrink-0', textColor)} />
        {richText ? (
          <div className={cn(textClass, textColor)}>{children}</div>
        ) : (
          <p className={cn(textClass, textColor)}>{children}</p>
        )}
      </div>
      {action && (
        <div
          className={cn(
            'typography-paragraph-xs underline decoration-solid self-end',
            textColor,
          )}
        >
          {action}
        </div>
      )}
    </div>
  );
}
